import type { AuditRecorder } from "../audit/audit-recorder.js";
import type { RequestContext } from "../application/context/request-context.js";
import type { DashboardShareRepository } from "../repositories/dashboard-share-repository.js";
import { resolveEffectivePermissions } from "../permissions/effective-permissions.js";
import {
  DashboardSharePublicService,
  DashboardShareService,
  ShareAccessGuard,
  type DashboardDataSources
} from "../application/services/dashboard-share-service.js";

export interface DashboardShareLookups {
  getUser(userId: string): Promise<{ status?: string; profileType?: string; customPermissions?: readonly string[]; professionalId?: string; name?: string } | null>;
  clientName(clientId: string): Promise<string | null>;
  /** O gate oficial de acesso ao Cliente (AdminService.assertUserHasAccessToClient, §37.1). */
  assertClientAccess(context: RequestContext, clientId: string): Promise<unknown>;
}

/**
 * Liga o link compartilhado do Painel. A mesma função nas duas composições.
 * Só `service` vai para o `/api/rpc`; `publicService` é chamado pelo
 * endpoint público do servidor e, no modo navegador, pela própria página.
 */
export function wireDashboardShare(
  repo: DashboardShareRepository,
  sources: DashboardDataSources,
  audit: AuditRecorder | undefined,
  lookups: DashboardShareLookups
): { service: DashboardShareService; publicService: DashboardSharePublicService } {
  const guard = new ShareAccessGuard();
  const deps = {
    repo,
    sources,
    audit,
    // O autor como está HOJE: inativo ou sem a permissão de compartilhar,
    // os links dele deixam de abrir (falha fechada).
    owner: async (userId: string) => {
      const user = await lookups.getUser(userId);
      if (!user) return null;
      return {
        active: user.status === "ACTIVE",
        permissions: resolveEffectivePermissions(user.profileType || "VIEWER", user.customPermissions),
        professionalId: user.professionalId ?? null,
        name: user.name ?? null
      };
    },
    clientAccess: async (userId: string, permissions: ReadonlySet<string>, clientId: string) => {
      try {
        await lookups.assertClientAccess({ activeClientId: clientId as any, correlationId: "" as any, userId: userId as any, professionalId: null, permissions: permissions as any }, clientId);
        return true;
      } catch {
        return false;
      }
    },
    clientName: (clientId: string) => lookups.clientName(clientId)
  };
  return { service: new DashboardShareService(deps, guard), publicService: new DashboardSharePublicService(deps, guard) };
}
